import { Component, OnChanges, SimpleChanges, EventEmitter } from '@angular/core';
import { DriverAttribute } from '../../../driver/models/credential/driver-attribute.model';
import { DriverCredentialAttribute } from '../../../driver/models/credential/driver-credential-attribute.model';
import { DriverAttributeCategoryType } from '../../../driver/models/enums/driver-attribute-category-type.enum';
import { DriverAttributeType } from '../../../driver/models/enums/driver-attribute-type.enum';
import { DriverAttributeAccessType } from '../../../driver/models/enums/driver-attribute-access-type.enum';
import { DriverCredentialApplicationItemSourceType } from '../../../driver/models/enums/driver-credential-application-item-source-type.enum';
import { DriverRestrictionService } from '../../../driver/services/driver-restriction.service';
import { ModalSize } from '../../../shared/services/modal/mvd-modal.enum';
import { MvdModalService } from '../../../shared/services/modal/mvd-modal.service';
import { CustomerMrpRestrictionModalComponent, CustomerMrpRestrictionModalComponentContext } from '../customer-mrp-restriction-modal/customer-mrp-restriction-modal.component';
import { DriverAttributeData } from '../../../driver/models/credential/driver-attribute-data.model';
import { BaseComponent } from '../../../shared/base-components/base.component';
import { RestrictionViewModel } from '../../models/restriction-viewmodel.model';
import { DriverAttributeviewModel } from '../../models/driver-attribute-viewmodel.model';
import { CustomerMrpRestrictionsEditModalComponent, CustomerMrpRestrictionsEditModalComponentContext } from '../customer-mrp-restrictions-edit-modal/customer-mrp-restrictions-edit-modal.component';

@Component({
    selector: 'customer-mrp-restrictions',
    templateUrl: './customer-mrp-restrictions.html',
    inputs: ['customerNumberId', 'isReadOnly'],
    outputs: ['onRefresh']
})
export class CustomerMrpRestrictionsComponent extends BaseComponent implements OnChanges {
    public customerNumberId: number;
    public driverAttributes: RestrictionViewModel[];
    public credentialAttributes: DriverAttributeviewModel[];
    public driverAttributeAccessType = new DriverAttributeAccessType();
    private driverAttributeTypeEnum = new DriverAttributeType();
    private driverAttributeCategoryTypeEnum = new DriverAttributeCategoryType();
    private applicationItemSourceTypeEnum = new DriverCredentialApplicationItemSourceType();
    public onRefresh: EventEmitter<boolean> = new EventEmitter<boolean>();
    public isReadOnly = false;

    constructor(
        private modalService: MvdModalService,
        private driverRestrictionService: DriverRestrictionService
    ) {
        super();
    }

    public ngOnInit() {}

    public ngOnChanges(changes: SimpleChanges) {
        if (null != changes.customerNumberId && changes.customerNumberId.currentValue > 0) {
            this.customerNumberId = changes.customerNumberId.currentValue;
            this.driverRestrictionService.getCustomerAttributes(changes.customerNumberId.currentValue)
                .subscribe(r => this.loadAttributes(r.result));
            this.driverRestrictionService.getActiverDriverCredentialAttributes(changes.customerNumberId.currentValue)
                .subscribe(r => this.loadDriverCredentialAttributes(r.result));
        }
    }

    public addRestriction = (): void => {
        let data = new CustomerMrpRestrictionModalComponentContext(this.customerNumberId);
        this.modalService.open(CustomerMrpRestrictionModalComponent, data, ModalSize.Medium)
            .afterClosed()
            .subscribe(() => {
                this.driverRestrictionService.getCustomerAttributes(this.customerNumberId)
                    .subscribe(r => this.loadAttributes(r.result));
            });
    };

    private loadAttributes = (attributes: DriverAttributeData[]): void => {
        if (null != attributes) {
            this.driverAttributes = attributes.map(e => this.mapDriverAttributeToMrpRestrictionViewModel(e));
        }
    };

    private mapDriverAttributeToMrpRestrictionViewModel = (attribute: DriverAttributeData): RestrictionViewModel => {
        let driverAttributeType = this.driverAttributeTypeEnum.FromId(attribute.driverAttributeTypeId);
        let source = this.applicationItemSourceTypeEnum.FromId(attribute.driverCredentialApplicationItemSourceTypeId);
        let mrpRestriction = new RestrictionViewModel();
        mrpRestriction.attributeTypeId = attribute.driverAttributeTypeId;
        mrpRestriction.code = 'UNKNOWN';
        mrpRestriction.displayValue = 'UNKNOWN';
        mrpRestriction.source = 'UNKNOWN';
        mrpRestriction.endDate = attribute.endDate;
        mrpRestriction.id = attribute.id;
        if (null != driverAttributeType) {
            mrpRestriction.code = driverAttributeType.driverAttributeValue;
            mrpRestriction.displayValue = driverAttributeType.displayValue;
            mrpRestriction.attributeCategory = this.driverAttributeCategoryTypeEnum.FromId(driverAttributeType.driverAttributeCategoryType).displayValue;
            mrpRestriction.updatedBy = attribute.updatedBy;
            mrpRestriction.updatedDate = attribute.updatedDate;
            mrpRestriction.attributeAccessTypeId = driverAttributeType.driverAttributeAccessTypeId;
            // If the added Restriction is of type, Other, display inputted description instead of the value from Enum
            if (mrpRestriction.attributeTypeId == this.driverAttributeTypeEnum.Other.id) {
                mrpRestriction.displayValue = attribute.description;
                if (mrpRestriction.displayValue === '' || mrpRestriction.displayValue == null ) {
                    mrpRestriction.displayValue = this.driverAttributeTypeEnum.Other.displayValue;
                }
            }
        }

        if (null != source) {
            mrpRestriction.source = source.displayValue;
        }

        return mrpRestriction;
    };

    private loadDriverCredentialAttributes = (attributes: DriverCredentialAttribute[]): void => {
        if (null != attributes) {
            this.credentialAttributes = attributes.map(e => this.mapDriverCredentialAttributeToMrpCredentialAttribute(e));
        }
    };

    private mapDriverCredentialAttributeToMrpCredentialAttribute = (attribute: DriverCredentialAttribute): DriverAttributeviewModel => {
        let driverAttributeType = this.driverAttributeTypeEnum.FromId(attribute.driverAttributeTypeId);
        let source = this.applicationItemSourceTypeEnum.FromId(attribute.driverCredentialApplicationItemSourceTypeId);
        let mrpDriverRestriction = new DriverAttributeviewModel();
        mrpDriverRestriction.id = attribute.id;
        mrpDriverRestriction.endDate = attribute.endDate;
        mrpDriverRestriction.driverAttributeTypeId = attribute.driverAttributeTypeId;
        mrpDriverRestriction.code = 'UNKNOWN';
        mrpDriverRestriction.displayValue = 'UNKNOWN';
        mrpDriverRestriction.source = 'UNKNOWN';

        if (null != driverAttributeType) {
            mrpDriverRestriction.code = driverAttributeType.driverAttributeValue;
            mrpDriverRestriction.displayValue = driverAttributeType.displayValue;
            mrpDriverRestriction.attributeCategory = this.driverAttributeCategoryTypeEnum.FromId(driverAttributeType.driverAttributeCategoryType).displayValue;
            mrpDriverRestriction.updatedBy = attribute.updatedBy;
            mrpDriverRestriction.updatedDate = attribute.updatedDate;
            mrpDriverRestriction.attributeAccessTypeId = driverAttributeType.driverAttributeAccessTypeId;
            // If the added Restriction is of type, Other, display inputted description instead of the value from Enum
            if (mrpDriverRestriction.driverAttributeTypeId == 22) {
                mrpDriverRestriction.displayValue = attribute.description;
            }
        }

        if (null != source) {
            mrpDriverRestriction.source = source.displayValue;
        }

        return mrpDriverRestriction;
    };

    public deleteRestriction = (restrictionId: number): void => {
        this.driverRestrictionService.deleteDriverRestriction(this.customerNumberId, restrictionId)
            .subscribe(
                r => this.refreshAll(),
                e => this.handleValidationResult(e)
            );
    };

    public editRestriction = (restrictionId: number): void => {
        let data = new CustomerMrpRestrictionsEditModalComponentContext(restrictionId, this.customerNumberId);
        this.modalService.open(CustomerMrpRestrictionsEditModalComponent, data, ModalSize.Medium)
            .afterClosed()
            .subscribe(() => {
                this.driverRestrictionService.getCustomerAttributes(this.customerNumberId)
                    .subscribe(r => this.loadAttributes(r.result));
            });
    };

    public deleteDriverCredentialAttribute = (driverCredentialAttributeId: number): void => {
        this.driverRestrictionService.deleteDriverCredentialAttribute(driverCredentialAttributeId, this.customerNumberId)
            .subscribe(
                r => this.refreshAll(),
                e => this.handleValidationResult(e));
    };

    private refreshAll = (): void => {
        this.driverRestrictionService.getActiverDriverCredentialAttributes(this.customerNumberId)
            .subscribe(r => this.loadDriverCredentialAttributes(r.result));
        this.driverRestrictionService.getCustomerAttributes(this.customerNumberId)
            .subscribe(r => this.loadAttributes(r.result));
        this.onRefresh.emit(true);

    };
}
